/**
 * Accent-color derivation (见 13 / §5.10). An accent preset is one base color
 * per mode; everything brand-colored in the palette is derived from it here, in
 * JS, so the theme that reaches every window / sandboxed surface already carries
 * literal values (no runtime `color-mix` dependency for consumers):
 *
 * - `brand` + `brand-a90` … `brand-a5`: the preset color and its alpha steps.
 * - `accent-1` … `accent-10` (+ `accent-N-a90`): a tonal ramp with the same step
 *   semantics as the Figma `indigo-N` ramp (1–2 light washes, 3 focus/disabled,
 *   5 hover, 6 the color itself, 7 active, 8–10 deep). Each step mixes the
 *   preset color toward a palette primitive in sRGB; the per-step ratios are
 *   fitted so the `brand` preset reproduces the indigo ramp as closely as a
 *   plain mix can (the indigo ramp itself stays a pure primitive).
 *
 * Presets may also override a few semantic tokens where the derived defaults
 * would be unreadable (only `yellow`: dark text on the accent fill).
 */
import type {
  ThemeAccent,
  ThemeBaseColors,
  ThemeTokens,
  ThemeType,
} from './theme-contract';
import darkTheme from './themes/dark.json';
import lightTheme from './themes/light.json';

/** Alpha steps of `brand-aNN` (percent), matching the Figma export. */
export const BRAND_ALPHA_STEPS = [
  90, 80, 70, 60, 50, 40, 30, 20, 10, 5,
] as const;

/** Steps of the derived `accent-N` ramp. */
export const ACCENT_RAMP_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

interface AccentMixStep {
  /** Base primitive the preset color is mixed toward. */
  toward: string;
  /** Share of `toward` in the mix (0 = the preset color itself). */
  ratio: number;
}

/**
 * Per-mode mix table for the `accent-N` ramp. Light washes toward `white` and
 * deepens toward `black`; dark mirrors the indigo dark ramp — low steps sink
 * toward the dark app background (`gray-07`), high steps lift toward `white`.
 * Ratios fitted against the indigo ramp with the `brand` preset (least squares
 * in sRGB, 0.005 grid).
 */
export const ACCENT_MIX_TABLE: Record<
  ThemeType,
  Record<(typeof ACCENT_RAMP_STEPS)[number], AccentMixStep>
> = {
  light: {
    1: { toward: 'white', ratio: 0.91 },
    2: { toward: 'white', ratio: 0.83 },
    3: { toward: 'white', ratio: 0.54 },
    4: { toward: 'white', ratio: 0.275 },
    5: { toward: 'white', ratio: 0.135 },
    6: { toward: 'white', ratio: 0 },
    7: { toward: 'black', ratio: 0.145 },
    8: { toward: 'black', ratio: 0.3 },
    9: { toward: 'black', ratio: 0.42 },
    10: { toward: 'black', ratio: 0.545 },
  },
  dark: {
    1: { toward: 'gray-07', ratio: 0.9 },
    2: { toward: 'gray-07', ratio: 0.855 },
    3: { toward: 'gray-07', ratio: 0.71 },
    4: { toward: 'gray-07', ratio: 0.575 },
    5: { toward: 'gray-07', ratio: 0.39 },
    6: { toward: 'gray-07', ratio: 0 },
    7: { toward: 'white', ratio: 0.145 },
    8: { toward: 'white', ratio: 0.41 },
    9: { toward: 'white', ratio: 0.77 },
    10: { toward: 'white', ratio: 0.865 },
  },
};

interface AccentPreset {
  /** Base primitive (in the built-in palette of each mode) holding the color. */
  source: string;
  /** Semantic-token overrides per mode (values are `var(--app-base-*)` refs). */
  tokens?: Partial<Record<ThemeType, ThemeTokens>>;
}

/** The {@link ThemeAccent} presets: color source + per-mode token overrides. */
export const ACCENT_PRESETS: Record<ThemeAccent, AccentPreset> = {
  brand: { source: 'brand' },
  red: { source: 'extended-red' },
  orange: { source: 'extended-orange' },
  // White text on yellow is unreadable, and a yellow `text-brand` on the light
  // canvas too: dark text on the fill, and the deeper ramp step for brand text.
  yellow: {
    source: 'extended-yellow',
    tokens: {
      light: {
        'color-on-accent': 'var(--app-base-black)',
        'color-text-brand': 'var(--app-base-accent-7)',
      },
      dark: { 'color-on-accent': 'var(--app-base-black)' },
    },
  },
  green: { source: 'extended-green' },
  mint: { source: 'extended-mint' },
  teal: { source: 'extended-teal' },
  cyan: { source: 'extended-cyan' },
  blue: { source: 'extended-blue' },
};

/** The built-in (default-accent) base palettes the presets read from. */
const BUILTIN_BASE: Record<ThemeType, ThemeBaseColors> = {
  light: lightTheme.base,
  dark: darkTheme.base,
};

type Rgb = [number, number, number];

const HEX_RE = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;

function parseHex(color: string): Rgb {
  const match = HEX_RE.exec(color);
  if (!match) {
    throw new Error(`accent derivation needs a #rrggbb color, got "${color}"`);
  }
  return [
    parseInt(match[1], 16),
    parseInt(match[2], 16),
    parseInt(match[3], 16),
  ];
}

function toHex(rgb: Rgb): string {
  return `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function toRgba(rgb: Rgb, alpha: number): string {
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

function mix(color: Rgb, toward: Rgb, ratio: number): Rgb {
  return [0, 1, 2].map((i) =>
    Math.round(color[i] * (1 - ratio) + toward[i] * ratio),
  ) as Rgb;
}

function readBase(
  base: ThemeBaseColors,
  name: string,
  type: ThemeType,
): string {
  const value = base[name];
  if (value === undefined) {
    throw new Error(`${type} palette has no base color "${name}"`);
  }
  return value;
}

/** The preset's base color for a mode (`brand` → the default brand color). */
export function accentPresetColor(
  accent: ThemeAccent,
  type: ThemeType,
): string {
  return readBase(BUILTIN_BASE[type], ACCENT_PRESETS[accent].source, type);
}

/**
 * Derive the accent-dependent base colors from one `#rrggbb` color for a mode:
 * `brand`, `brand-aNN`, `accent-N` and `accent-N-a90`. `palette` supplies the
 * mix targets named in {@link ACCENT_MIX_TABLE}. Pure.
 */
export function deriveAccentBase(
  color: string,
  type: ThemeType,
  palette: ThemeBaseColors = BUILTIN_BASE[type],
): ThemeBaseColors {
  const rgb = parseHex(color);
  const out: ThemeBaseColors = { brand: toHex(rgb) };
  for (const step of BRAND_ALPHA_STEPS) {
    out[`brand-a${step}`] = toRgba(rgb, step / 100);
  }
  for (const step of ACCENT_RAMP_STEPS) {
    const { toward, ratio } = ACCENT_MIX_TABLE[type][step];
    const mixed = mix(rgb, parseHex(readBase(palette, toward, type)), ratio);
    out[`accent-${step}`] = toHex(mixed);
    out[`accent-${step}-a90`] = toRgba(mixed, 0.9);
  }
  return out;
}

/** The semantic-token overrides a preset applies in a mode (may be empty). */
export function accentTokenOverrides(
  accent: ThemeAccent,
  type: ThemeType,
): ThemeTokens {
  return ACCENT_PRESETS[accent].tokens?.[type] ?? {};
}
